import React, { useContext, useEffect, useRef, useState } from 'react'
import { Pattern } from '../contexts/PatternContext'
import {
  ConditionGroupType,
  ConditionType,
  PatternTreeContext,
} from '../contexts/PatternTreeContext'
import { useCopilotHighlights } from '../copilot/contexts/PatternCopilotContext'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha, styled, useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import AddIcon from '@mui/icons-material/Add'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AssessmentIcon from '@mui/icons-material/Assessment'
import TriggerChartSelector from './TriggerChartSelector'
import getConditionLabel from '../../../common/utils/getConditionLabel'
import { getConditionTypes } from '../condition/conditions'
import ConditionSelector from '../condition/ConditionSelector'
import PreviousCandleSettings from '../condition/components/PreviousCandleSettings'
import PrerequisiteSettings from '../condition/components/PrerequisiteSettings'
import ValidityWindowSettings from '../condition/components/ValidityWindowSettings'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import InfoIcon from '@mui/icons-material/Info'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import UndoIcon from '@mui/icons-material/Undo'
import TimerIcon from '@mui/icons-material/Timer'
import { usePatternSidePanelContext } from '../contexts/PatternSidePanelContext'
import Divider from '@mui/material/Divider'
import ConditionGroupCountSelect from './ConditionGroupCountSelect'
import './PatternListItem.css'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { ChartDetailInput } from './ChartDetailInput'
import { useDrag, useDrop } from 'react-dnd'
import { MAX_CONDITION_GROUP_DEPTH } from '../../../common/constants'
import CloseIcon from '@mui/icons-material/Close'
import { ArrowRightAlt, Close, ContentCopy, Save } from '@mui/icons-material'
import { useAlertContext } from '../../alerts/contexts/AlertContext'
import { collection, getCountFromServer, getDocs, query, where } from 'firebase/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { deletePattern } from '../../../common/utils/deletePattern'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import { useSavePattern } from '../../../hooks/useSavePattern'
import { useCopyPatternWithSave } from '../../../hooks/useCopyPatternToAccount'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid'
import ShareDialog from './sidebar/patternInput/ShareDialog'
import LibraryAddIcon from '@mui/icons-material/LibraryAdd'
import SavedConditionsList from './SavedConditionsList'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { v4 as uuidv4 } from 'uuid'
import { regeneratePatternIds } from '../../../common/utils/regeneratePatternIds'
import ReactGA from 'react-ga4'
import generateShareLink from '../../../common/utils/generateShareLink'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import ReplyIcon from '@mui/icons-material/Reply'
import { PreviewConflictContext } from '../preview'

const HighlightedPrereqContext = React.createContext<{
  highlightedIds: Set<string>
  setHighlightedIds: (ids: Set<string>) => void
}>({ highlightedIds: new Set(), setHighlightedIds: () => {} })

const PatternItemWrapper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  overflow: 'hidden',
  backgroundColor: theme.palette.background.custom2,
  borderRadius: '8px',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
}))

const PatternHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  cursor: 'pointer',
  borderLeft: '3px solid transparent',
  transition: 'all 0.15s ease',
  '&:hover': {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
  },
}))

const PatternContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}))

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  '& > button': {
    marginLeft: theme.spacing(1),
  },
}))

const GroupItem = styled(ListItem)(({ theme }) => ({
  borderRadius: '6px',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    '& .group-action-button': {
      opacity: 1,
    },
  },
  marginBottom: theme.spacing(1),
  border: '1px solid rgba(255, 255, 255, 0.06)',
  padding: theme.spacing(1, 1.5),
  transition: 'background-color 0.15s ease',
}))

// Add an interface for the custom props
interface GroupActionButtonProps {
  textcolor?: string
  hoverbgcolor?: string
}

const GroupActionButton = styled(IconButton)<GroupActionButtonProps>(
  ({ theme, textcolor, hoverbgcolor }) => ({
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    margin: '0 4px',
    '&:hover': {
      backgroundColor: hoverbgcolor || theme.palette.error.main + '20',
      color: textcolor || theme.palette.error.main,
    },
  })
)

const ConditionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingLeft: `calc(${theme.spacing(2)} + 2px)`,
  paddingTop: theme.spacing(1),
  marginTop: theme.spacing(1),
  width: '100%',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(2),
    top: theme.spacing(-1),
    bottom: 0,
    width: 2,
    height: 'auto',
    backgroundColor: theme.palette.divider,
  },
}))

const GroupContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingLeft: `calc(${theme.spacing(2)} + 2px)`,
  paddingTop: theme.spacing(1),
  marginTop: theme.spacing(1),
  width: '100%',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(2),
    top: theme.spacing(-1),
    bottom: 0,
    width: 2,
    height: 'auto',
    minHeight: '50px',
    backgroundColor: theme.palette.divider,
  },
}))

const ConnectionDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: `calc(${theme.spacing(-1)} + 1px)`,
  top: `calc(${theme.spacing(-2)} + 2px)`,
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.custom4,
  cursor: 'pointer',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  '&::before': {
    content: '"+"',
    color: theme.palette.text.primary,
    opacity: 0.5,
    fontSize: '10px',
    lineHeight: 1,
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    transform: 'scale(1.5)',
    boxShadow: '0 0 10px rgba(29, 195, 14, 0.4)',
    '&::before': {
      content: '"+"',
      color: 'white',
      fontSize: '10px',
      fontWeight: 'bold',
      opacity: 1,
    },
  },
}))

const GroupItemContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(3),
  width: '100%',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: theme.spacing(2),
    width: theme.spacing(3),
    height: 2,
    backgroundColor: theme.palette.divider,
    zIndex: 1,
  },
  '&:last-child': {
    marginBottom: 0,
  },
  '&:hover': {
    '& .group-action-button': {
      opacity: 1,
    },
  },
}))

const ConditionItemContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(3),
  width: '100%',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: theme.spacing(2),
    width: theme.spacing(3),
    height: 2,
    backgroundColor: theme.palette.divider,
    zIndex: 1,
  },
  '&:last-child': {
    marginBottom: 0,
  },
  '&:hover': {
    '& .delete-icon-button': {
      opacity: 1,
    },
  },
}))

const ConditionItem = styled(Paper)(({ theme }) => ({
  borderRadius: '6px',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '100%',
  padding: 0,
  backgroundColor: theme.palette.background.custom2,
  '&:hover': {
    backgroundColor: theme.palette.background.custom3,
  },
  border: '1px solid rgba(255, 255, 255, 0.06)',
  marginLeft: 0,
  marginRight: 0,
  overflow: 'hidden',
  boxSizing: 'border-box',
  transition: 'background-color 0.15s ease',
}))

const ConditionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(1, 1.5),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.background.custom3,
  },
  boxSizing: 'border-box',
  transition: 'background-color 0.15s ease',
}))

const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  margin: '0 4px',
  '&:hover': {
    backgroundColor: theme.palette.error.main + '20',
    color: theme.palette.error.main,
  },
}))

const ControlsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(3),
}))

const ConditionGroupsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}))

const DragHandle = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'rgba(255, 255, 255, 0.25)',
  cursor: 'grab',
  transition: 'color 0.15s ease',
  '&:hover': {
    color: 'rgba(255, 255, 255, 0.6)',
  },
  marginRight: theme.spacing(1),
}))

// Add more styled components to match Condition.tsx styling
const CenteredDividerContainer = styled('div')(({ theme }) => ({
  width: '100%',
  ...theme.typography.body2,
  '& > :not(style) + :not(style)': {
    marginTop: theme.spacing(2),
  },
}))

// ConditionListItem Component
interface ConditionListItemProps {
  condition: ConditionType
  sourceGroupId: string
  isReadOnly?: boolean
}

function ConditionListItem({
  condition,
  sourceGroupId,
  isReadOnly = false,
}: ConditionListItemProps) {
  const { highlightedIds, setHighlightedIds } = useContext(HighlightedPrereqContext)
  const { conflictConditionIds } = useContext(PreviewConflictContext)
  // Direct import path (not the copilot barrel) keeps react-markdown out of
  // this module graph; safe default outside the provider.
  const { highlightConditionIds: copilotFlashIds } = useCopilotHighlights()
  const { newlyCreatedConditionIds } = useContext(PatternTreeContext)
  const shouldAutoOpen = newlyCreatedConditionIds.current.has(condition.id)
  if (shouldAutoOpen) {
    newlyCreatedConditionIds.current.delete(condition.id)
  }
  const [isEditing, setIsEditing] = useState(shouldAutoOpen)
  const [isHovering, setIsHovering] = useState(false)
  const [anchorEl, setAnchorEl] = useState<any>(null)
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null)
  const closeTimer = useRef<NodeJS.Timeout | null>(null)
  const [isPrereqHovering, setIsPrereqHovering] = useState(false)
  const [prereqAnchorEl, setPrereqAnchorEl] = useState<any>(null)
  const [prereqArrowRef, setPrereqArrowRef] = useState<HTMLDivElement | null>(null)
  const prereqCloseTimer = useRef<NodeJS.Timeout | null>(null)
  const [isValidityHovering, setIsValidityHovering] = useState(false)
  const [validityAnchorEl, setValidityAnchorEl] = useState<any>(null)
  const [validityArrowRef, setValidityArrowRef] = useState<HTMLDivElement | null>(null)
  const validityCloseTimer = useRef<NodeJS.Timeout | null>(null)
  const [isTargetHovering, setIsTargetHovering] = useState(false)
  const [targetAnchorEl, setTargetAnchorEl] = useState<any>(null)
  const [targetArrowRef, setTargetArrowRef] = useState<HTMLDivElement | null>(null)
  const targetCloseTimer = useRef<NodeJS.Timeout | null>(null)
  const [showTargetInfo, setShowTargetInfo] = useState(false)
  const theme = useTheme()
  const { handleChangeCondition, handleRemoveCondition, pattern } = useContext(PatternTreeContext)

  // Determine initial candle mode based on existing settings
  const getInitialCandleMode = (): 'target' | null => {
    if (condition.condition?.LHS?.candle || condition.condition?.RHS?.candle) {
      return 'target'
    }
    return null
  }

  const [candleMode, setCandleMode] = useState<'target' | null>(getInitialCandleMode())

  // Handle target candle mode toggle
  const handleCandleModeChange = () => {
    if (candleMode === 'target') {
      // Deselect target mode
      setCandleMode(null)
      const updates: any = {}
      if (condition.condition?.LHS?.candle) {
        updates['condition.LHS.candle'] = undefined
      }
      if (condition.condition?.RHS?.candle) {
        updates['condition.RHS.candle'] = undefined
      }
      if (Object.keys(updates).length > 0) {
        handleChangeCondition(sourceGroupId, condition.id, updates)
      }
    } else {
      // Enable target mode, disable previous candle
      setCandleMode('target')
      handleChangeCondition(sourceGroupId, condition.id, {
        'settings.previousCandle.enabled': false,
      })
    }
  }

  // Add drag functionality
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'condition',
    item: {
      id: condition.id,
      type: 'condition',
      sourceGroupId,
      patternId: pattern?.id || '',
    },
    canDrag: !isReadOnly,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }))

  const handleConditionClick = () => {
    if (isReadOnly) return
    setIsEditing(!isEditing)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isReadOnly) return
    handleRemoveCondition(sourceGroupId, condition.id)
  }

  function _onChangeCondition(key: string, value: any) {
    if (isReadOnly) return
    let updates: { [key: string]: any } = {
      [`condition.${key}`]: value,
    }

    // Apply all changes
    handleChangeCondition(sourceGroupId, condition.id, updates)
  }

  const handleIconHover = (event: any) => {
    if (isReadOnly) return
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setAnchorEl(event.currentTarget)
    setIsHovering(true)
  }

  const handlePopoverEnter = () => {
    if (isReadOnly) return
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setIsHovering(true)
  }

  const handlePopoverLeave = () => {
    if (isReadOnly) return
    closeTimer.current = setTimeout(() => {
      setIsHovering(false)
      setAnchorEl(null)
    }, 300)
  }

  const handlePrereqIconHover = (event: any) => {
    if (isReadOnly) return
    if (prereqCloseTimer.current) clearTimeout(prereqCloseTimer.current)
    setPrereqAnchorEl(event.currentTarget)
    setIsPrereqHovering(true)
  }

  const handlePrereqPopoverEnter = () => {
    if (isReadOnly) return
    if (prereqCloseTimer.current) clearTimeout(prereqCloseTimer.current)
    setIsPrereqHovering(true)
  }

  const handlePrereqPopoverLeave = () => {
    if (isReadOnly) return
    prereqCloseTimer.current = setTimeout(() => {
      setIsPrereqHovering(false)
      setPrereqAnchorEl(null)
    }, 300)
  }

  const handleValidityIconHover = (event: any) => {
    if (isReadOnly) return
    if (validityCloseTimer.current) clearTimeout(validityCloseTimer.current)
    setValidityAnchorEl(event.currentTarget)
    setIsValidityHovering(true)
  }

  const handleValidityPopoverEnter = () => {
    if (isReadOnly) return
    if (validityCloseTimer.current) clearTimeout(validityCloseTimer.current)
    setIsValidityHovering(true)
  }

  const handleValidityPopoverLeave = () => {
    if (isReadOnly) return
    validityCloseTimer.current = setTimeout(() => {
      setIsValidityHovering(false)
      setValidityAnchorEl(null)
    }, 300)
  }

  const handleTargetIconHover = (event: any) => {
    if (isReadOnly) return
    if (targetCloseTimer.current) clearTimeout(targetCloseTimer.current)
    setTargetAnchorEl(event.currentTarget)
    setIsTargetHovering(true)
  }

  const handleTargetPopoverEnter = () => {
    if (isReadOnly) return
    if (targetCloseTimer.current) clearTimeout(targetCloseTimer.current)
    setIsTargetHovering(true)
  }

  const handleTargetPopoverLeave = () => {
    if (isReadOnly) return
    targetCloseTimer.current = setTimeout(() => {
      setIsTargetHovering(false)
      setTargetAnchorEl(null)
    }, 300)
  }

  // Create wrapper function for setConditionIsValid that matches the signature expected by ConditionSelector
  const setConditionIsValidWrapper = (isValid: boolean) => {
    if (isReadOnly) return
    handleChangeCondition(sourceGroupId, condition.id, { 'condition.isValid': isValid })
  }

  // Compute effective validity: stored isValid AND threshold check
  const isConditionInvalid = (() => {
    if (!condition.condition.isValid) return true
    const ct = getConditionTypes(condition?.condition?.LHS?.field)?.find(
      (c) => c.value === condition?.condition?.conditionType
    )
    if (ct?.hasThreshold && condition?.condition?.threshold === undefined) return true
    return false
  })()

  return (
    <ConditionItemContainer>
      <ConditionItem
        data-tour-id="pattern-condition"
        data-condition-id={condition.id}
        sx={{
          // Precedence: transient prereq-hover/copilot-flash green → invalid
          // red → preview conflict amber → default divider (spec §9 — amber
          // never outranks red)
          borderColor:
            highlightedIds.has(condition.id) || copilotFlashIds.has(condition.id)
              ? theme.palette.primary.main
              : isConditionInvalid
                ? 'rgb(201,80,80)'
                : conflictConditionIds.includes(condition.id)
                  ? theme.palette.warning.main
                  : theme.palette.divider,
          boxShadow:
            highlightedIds.has(condition.id) || copilotFlashIds.has(condition.id)
              ? `0px 0px 8px ${alpha(theme.palette.primary.main, 0.4)}`
              : isConditionInvalid
                ? '0px 0px 10px rgb(196 127 127)'
                : conflictConditionIds.includes(condition.id)
                  ? `0px 0px 8px ${alpha(theme.palette.warning.main, 0.35)}`
                  : 'none',
          opacity: isDragging ? 0.5 : 1,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        }}
      >
        <ConditionHeader
          data-tour-id="pattern-condition-header"
          onClick={handleConditionClick}
          ref={!isReadOnly ? drag : null}
        >
          {!isReadOnly && (
            <DragHandle
              onClick={(e) => e.stopPropagation()}
              data-tour-id="pattern-condition-drag-handle"
            >
              <DragIndicatorIcon fontSize="small" />
            </DragHandle>
          )}
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.primary">
                  {getConditionLabel(condition)}
                </Typography>
                {condition.settings?.prerequisites?.length &&
                  condition.settings.prerequisites.length > 0 && (
                    <Tooltip
                      title={`Has ${condition.settings.prerequisites.length} prerequisite${
                        condition.settings.prerequisites.length !== 1 ? 's' : ''
                      }`}
                    >
                      <Chip
                        icon={<FormatListNumberedIcon sx={{ fontSize: '0.9rem !important' }} />}
                        label={condition.settings.prerequisites.length}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '0.7rem',
                        }}
                        onMouseEnter={() => {
                          const ids = new Set<string>(
                            (condition.settings?.prerequisites || []).map((p: any) => p.id)
                          )
                          setHighlightedIds(ids)
                        }}
                        onMouseLeave={() => setHighlightedIds(new Set())}
                      />
                    </Tooltip>
                  )}
              </Box>
            }
          />
          {!isReadOnly && (
            <DeleteIconButton
              size="small"
              onClick={handleDelete}
              sx={{ ml: 1 }}
              className="delete-icon-button"
            >
              <Tooltip title="Remove condition">
                <DeleteOutlineIcon fontSize="small" />
              </Tooltip>
            </DeleteIconButton>
          )}
        </ConditionHeader>

        <Collapse in={isEditing} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
          <Box
            sx={{
              padding: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              backgroundColor: theme.palette.background.custom2,
            }}
          >
            <ConditionSelector
              condition={condition.condition}
              onChangeCondition={_onChangeCondition}
              setConditionIsValid={setConditionIsValidWrapper}
              viewOnly={isReadOnly}
              candleMode={candleMode}
            />

            {!isReadOnly && (
              <>
                <CenteredDividerContainer>
                  <Divider textAlign={'center'}>
                    <Chip label="Settings" size={'small'} />
                  </Divider>
                </CenteredDividerContainer>

                <Box display={'flex'} alignItems={'center'} p={1} gap={2} flexWrap={'wrap'}>
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    gap={1}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <GpsFixedIcon
                      fontSize={'small'}
                      className={`condition-setting-icon ${candleMode === 'target' ? 'active' : 'default'}`}
                      onMouseEnter={handleTargetIconHover}
                      onMouseLeave={handleTargetPopoverLeave}
                      sx={{
                        cursor: 'pointer',
                        color:
                          candleMode === 'target'
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.primary.light,
                        },
                      }}
                    />
                    <UndoIcon
                      fontSize={'small'}
                      className={`condition-setting-icon ${condition.settings?.previousCandle?.enabled ? 'active' : 'default'}`}
                      onMouseEnter={handleIconHover}
                      onMouseLeave={handlePopoverLeave}
                      sx={{
                        cursor: 'pointer',
                        color: condition.settings?.previousCandle?.enabled
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.primary.light,
                        },
                      }}
                    />
                  </Box>
                  <FormatListNumberedIcon
                    fontSize={'small'}
                    className={`condition-setting-icon ${condition.settings?.prerequisites?.length ? 'active' : 'default'}`}
                    onMouseEnter={handlePrereqIconHover}
                    onMouseLeave={handlePrereqPopoverLeave}
                    sx={{
                      cursor: 'pointer',
                      color: condition.settings?.prerequisites?.length
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.primary.light,
                      },
                    }}
                  />
                  <TimerIcon
                    fontSize={'small'}
                    className={`condition-setting-icon ${condition.settings?.validityWindow?.candles ? 'active' : 'default'}`}
                    onMouseEnter={handleValidityIconHover}
                    onMouseLeave={handleValidityPopoverLeave}
                    sx={{
                      cursor: 'pointer',
                      color: condition.settings?.validityWindow?.candles
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.primary.light,
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </ConditionItem>

      {!isReadOnly && anchorEl && (
        <Popper
          open={isHovering && !!anchorEl}
          anchorEl={anchorEl}
          placement="bottom-start"
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
          disablePortal={false}
          sx={{
            zIndex: 1,
          }}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'clippingParents',
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: arrowRef,
              },
            },
          ]}
          className="popper"
        >
          <Box
            sx={{
              position: 'absolute',
              fontSize: 7,
              top: 0,
              left: 10,
              marginTop: '-0.9em',
              width: '3em',
              height: '1em',
              '&::before': {
                content: '""',
                margin: 'auto',
                display: 'block',
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 1em 1em 1em',
                borderColor: `transparent transparent ${theme.palette.background.custom1} transparent`,
              },
            }}
            ref={setArrowRef}
            className="arrow"
          />
          <PreviousCandleSettings
            condition={condition}
            sourceGroupId={sourceGroupId}
            candleMode={candleMode}
            setCandleMode={setCandleMode}
          />
        </Popper>
      )}

      {!isReadOnly && (
        <Popper
          open={isPrereqHovering && !!prereqAnchorEl}
          anchorEl={prereqAnchorEl}
          placement="bottom-start"
          onMouseEnter={handlePrereqPopoverEnter}
          onMouseLeave={handlePrereqPopoverLeave}
          disablePortal={false}
          sx={{
            zIndex: 1,
          }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: prereqArrowRef,
              },
            },
          ]}
          className="popper"
        >
          <Box
            sx={{
              position: 'absolute',
              fontSize: 7,
              top: 0,
              left: 10,
              marginTop: '-0.9em',
              width: '3em',
              height: '1em',
              '&::before': {
                content: '""',
                margin: 'auto',
                display: 'block',
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 1em 1em 1em',
                borderColor: `transparent transparent ${theme.palette.background.custom1} transparent`,
              },
            }}
            ref={setPrereqArrowRef}
            className="arrow"
          />
          <PrerequisiteSettings condition={condition} sourceGroupId={sourceGroupId} />
        </Popper>
      )}

      {!isReadOnly && (
        <Popper
          open={isValidityHovering && !!validityAnchorEl}
          anchorEl={validityAnchorEl}
          placement="bottom-start"
          onMouseEnter={handleValidityPopoverEnter}
          onMouseLeave={handleValidityPopoverLeave}
          disablePortal={false}
          sx={{
            zIndex: 1,
          }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: validityArrowRef,
              },
            },
          ]}
          className="popper"
        >
          <Box
            sx={{
              position: 'absolute',
              fontSize: 7,
              top: 0,
              left: 10,
              marginTop: '-0.9em',
              width: '3em',
              height: '1em',
              '&::before': {
                content: '""',
                margin: 'auto',
                display: 'block',
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 1em 1em 1em',
                borderColor: `transparent transparent ${theme.palette.background.custom1} transparent`,
              },
            }}
            ref={setValidityArrowRef}
            className="arrow"
          />
          <ValidityWindowSettings condition={condition} sourceGroupId={sourceGroupId} />
        </Popper>
      )}

      {!isReadOnly && (
        <Popper
          open={isTargetHovering && !!targetAnchorEl}
          anchorEl={targetAnchorEl}
          placement="bottom-start"
          onMouseEnter={handleTargetPopoverEnter}
          onMouseLeave={handleTargetPopoverLeave}
          disablePortal={false}
          sx={{
            zIndex: 1,
          }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: targetArrowRef,
              },
            },
          ]}
          className="popper"
        >
          <Box
            sx={{
              position: 'absolute',
              fontSize: 7,
              top: 0,
              left: 10,
              marginTop: '-0.9em',
              width: '3em',
              height: '1em',
              '&::before': {
                content: '""',
                margin: 'auto',
                display: 'block',
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 1em 1em 1em',
                borderColor: `transparent transparent ${theme.palette.background.custom1} transparent`,
              },
            }}
            ref={setTargetArrowRef}
            className="arrow"
          />
          <Paper
            sx={{
              p: 2,
              maxWidth: 300,
              background: theme.palette.background.custom1,
              gap: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1">Candle Target</Typography>
                <InfoIcon
                  sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'pointer' }}
                  onClick={() => setShowTargetInfo(!showTargetInfo)}
                />
              </Box>
              {candleMode === 'target' && (
                <Switch size="small" checked onChange={handleCandleModeChange} />
              )}
            </Box>
            <Collapse in={showTargetInfo}>
              <Typography variant="body2" color="textSecondary">
                Target a specific candle for each side of the condition instead of using the current
                candle. Useful for comparing values across different points in time.
              </Typography>
            </Collapse>
            {candleMode !== 'target' && (
              <>
                {condition.settings?.previousCandle?.enabled && (
                  <Typography variant="caption" color="warning.main">
                    Enabling will disable Previous Candle.
                  </Typography>
                )}
                <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="body2" color="textSecondary">
                    Enable
                  </Typography>
                  <Switch size="small" checked={false} onChange={handleCandleModeChange} />
                </Box>
              </>
            )}
          </Paper>
        </Popper>
      )}
    </ConditionItemContainer>
  )
}

// Add Item Button Component with Menu
function AddItemButton({ groupId }: { groupId: string }) {
  const { handleAddCondition, handleAddNestedGroup, pattern } = useContext(PatternTreeContext)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isSavedConditionListDrawerOpen, setIsSavedConditionListDrawerOpen] = useState(false)
  const open = Boolean(anchorEl)
  const theme = useTheme()

  // Find the group by ID to check its depth
  const findGroupById = (groups: ConditionGroupType[], id: string): ConditionGroupType | null => {
    for (const group of groups) {
      if (group.id === id) {
        return group
      }

      const found = findGroupById(group.conditionGroups, id)
      if (found) {
        return found
      }
    }
    return null
  }

  // Find the current group from either buy or sell condition groups
  const currentGroup = pattern
    ? findGroupById(pattern.buyConditionGroups, groupId) ||
      findGroupById(pattern.sellConditionGroups, groupId)
    : null

  // Check if the group has reached max depth
  const hasReachedMaxDepth = currentGroup ? currentGroup.depth >= MAX_CONDITION_GROUP_DEPTH : false

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleAddConditionClick = () => {
    handleAddCondition(groupId)
    handleClose()
  }

  const handleAddGroupClick = () => {
    handleAddNestedGroup(groupId)
    handleClose()
  }

  function handleOpenSavedConditionListDrawer() {
    setIsSavedConditionListDrawerOpen(true)
    handleClose()
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          ml: 0.25,
          color: theme.palette.text.primary,
          bgcolor: theme.palette.background.custom4,
          '&:hover': {
            bgcolor: theme.palette.action.hover,
          },
        }}
        data-tour-id="add-item-button"
      >
        <AddIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleAddConditionClick}>
          <SpeakerNotesOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          New Condition
        </MenuItem>
        {!hasReachedMaxDepth && [
          <MenuItem onClick={handleAddGroupClick}>
            <PlaylistAddIcon fontSize="small" sx={{ mr: 1 }} />
            New Group
          </MenuItem>,
          <MenuItem onClick={handleOpenSavedConditionListDrawer}>
            <LibraryAddIcon fontSize="small" sx={{ mr: 1 }} />
            Saved Group
          </MenuItem>,
        ]}
      </Menu>
      <Drawer
        anchor={'right'}
        open={isSavedConditionListDrawerOpen}
        onClose={() => setIsSavedConditionListDrawerOpen(false)}
      >
        <Box sx={{ padding: 2, maxWidth: '75vw' }}>
          <Box display={'flex'} alignItems={'center'}>
            <Typography variant="h6" flexGrow={1}>
              Your Saved Conditions and Groups
            </Typography>
            <Tooltip title={'Close'} arrow>
              <IconButton onClick={() => setIsSavedConditionListDrawerOpen(false)}>
                <Close />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider textAlign="left" sx={{ margin: '20px 0' }}>
            Choose a condition or a condition group to add to your pattern.
          </Divider>
          <SavedConditionsList groupId={groupId} />
        </Box>
      </Drawer>
    </>
  )
}

// Converts a number to an alphabetic representation (A, B, C, ..., Z, AA, AB, etc.)
function getAlphabeticSuffix(index: number): string {
  if (index < 0) return ''

  const getChar = (n: number) => String.fromCharCode(65 + (n % 26)) // 65 is ASCII for 'A'

  if (index < 26) {
    return getChar(index)
  }

  // For indices >= 26, use multiple characters (AA, AB, etc.)
  let result = ''
  let value = index

  do {
    const remainder = value % 26
    result = getChar(remainder) + result
    value = Math.floor(value / 26) - 1 // Subtract 1 for correct handling of AA, AB, etc.
  } while (value >= 0)

  return result
}

// Calculate the algorithmic title for a condition group
function getAlgorithmicGroupTitle(pattern: Pattern | undefined, groupId: string): string {
  if (!pattern) return 'Group'

  const buyGroups = pattern.buyConditionGroups || []
  const sellGroups = pattern.sellConditionGroups || []

  // Use both buy and sell groups for the traversal
  const allGroups = [...buyGroups, ...sellGroups]

  // Counter for groups encountered during traversal
  let groupCounter = 0
  // Flag to indicate if target group was found
  let foundTarget = false
  // Depth of the target group when found
  let targetDepth = 0

  // Recursive function to traverse groups in depth-first order
  const traverseGroups = (groups: ConditionGroupType[], currentDepth: number = 1): void => {
    if (foundTarget) return // Stop traversal if target already found

    for (const group of groups) {
      groupCounter++

      if (group.id === groupId) {
        foundTarget = true
        targetDepth = currentDepth
        return
      }

      // Traverse nested groups (depth-first)
      if (group.conditionGroups && group.conditionGroups.length > 0) {
        traverseGroups(group.conditionGroups, currentDepth + 1)
        if (foundTarget) return // Stop if found in nested groups
      }
    }
  }

  // Start the traversal
  traverseGroups(allGroups)

  if (!foundTarget) {
    return 'Group' // Return default if group not found
  }

  // Convert to 1-based index for display and get alphabetic suffix
  const suffix = getAlphabeticSuffix(groupCounter - 1)

  return `Group ${targetDepth}${suffix}`
}

// ConditionGroupListItem Component
interface ConditionGroupListItemProps {
  group: ConditionGroupType
  depth?: number
  isReadOnly?: boolean
}

function ConditionGroupListItem({
  group,
  depth = 0,
  isReadOnly = false,
}: ConditionGroupListItemProps) {
  const { highlightedIds } = useContext(HighlightedPrereqContext)
  // Calculate the total number of conditions (direct conditions + nested groups)
  const totalConditions = (group.conditionGroups?.length || 0) + (group.conditions?.length || 0)

  const { tradingHouse } = useApplicationSuiteContext()

  const {
    handleRemoveConditionGroup,
    handleChangeConditionGroup,
    handleConditionGroupCopyTo,
    handleAddCondition,
    handleDrop,
    handleConditionGroupDuplicate,
    activeTab,
    pattern,
  } = useContext(PatternTreeContext)

  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [dropIndex, setDropIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)

  // Add state for title editing
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState(group.groupTitle || '')

  // Generate algorithmic title
  const algorithmicTitle = getAlgorithmicGroupTitle(pattern, group.id)

  // Add drag functionality
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'group',
    item: {
      id: group.id,
      type: 'group',
      sourceGroupId: group.sourceGroupId || group.id,
      depth: group.depth,
      patternId: pattern?.id || '',
    },
    canDrag: !isReadOnly,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }))

  // Add drop functionality
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ['condition', 'group'],
      canDrop: (item: any) => {
        // Only allow drops from the same pattern and not in read-only mode
        return !isReadOnly && item.patternId === pattern?.id
      },
      drop: (item: any, monitor) => {
        if (isReadOnly) return

        // Check if it's a nested drop
        if (monitor.didDrop()) {
          return
        }

        // Only handle drops from the same pattern
        if (item.patternId !== pattern?.id) {
          return
        }

        if (handleDrop) {
          handleDrop(group.id, item, dropIndex >= 0 ? dropIndex : 0)
        }
      },
      hover: (item, monitor) => {
        if (isReadOnly) return

        // Only process hover for items from the same pattern
        if (item.patternId !== pattern?.id) {
          return
        }

        // Prevent the drag event from propagating to parent drop targets
        monitor.isOver({ shallow: true })

        if (!ref.current) return

        const hoverBoundingRect = ref.current.getBoundingClientRect()
        const clientOffset = monitor.getClientOffset()!
        const hoverClientY = clientOffset.y - hoverBoundingRect.top

        // Calculate drop position based on conditions in the group
        let dropPosition = 0
        if (group.conditions.length > 0) {
          for (let i = 0; i < group.conditions.length; i++) {
            const conditionElement = ref.current.querySelector(
              `[data-condition-id="${group.conditions[i].id}"]`
            )
            if (!conditionElement) continue

            const conditionRect = (conditionElement as HTMLElement).getBoundingClientRect()
            if (hoverClientY < conditionRect.top - hoverBoundingRect.top) {
              dropPosition = i
              break
            }
          }

          if (dropPosition === group.conditions.length) {
            dropPosition = group.conditions.length
          }
        }

        if (dropPosition === dropIndex) return

        setDropIndex(dropPosition)
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }) && monitor.getItem()?.patternId === pattern?.id,
        canDrop: monitor.canDrop() && monitor.getItem()?.patternId === pattern?.id,
        itemType: monitor.getItemType(),
        dragId: monitor.isOver() && monitor.getItem().id,
      }),
    }),
    [group.id, handleDrop, dropIndex, group.conditions, pattern?.id, isReadOnly]
  )

  // Combine drag and drop refs
  const dragDropRef = (el: HTMLElement | null) => {
    if (!isReadOnly) {
      drag(el)
      drop(el)
    }
  }

  function handleSaveConditionGroup() {
    const savedConditionGroups = localStorage.getItem(`savedConditions-${tradingHouse}`)
    const savedConditionGroupsArray = savedConditionGroups ? JSON.parse(savedConditionGroups) : []

    const legacySavedConditions = localStorage.getItem(`savedConditions`)
    let savedLegacyConditionGroupsArray = legacySavedConditions
      ? JSON.parse(legacySavedConditions)
      : []

    if (legacySavedConditions) {
      localStorage.setItem(`savedConditions-${tradingHouse}`, legacySavedConditions)
      localStorage.removeItem(`savedConditions`)

      savedConditionGroupsArray.push(...savedLegacyConditionGroupsArray)
    }

    const copyOfConditionGroup = JSON.parse(JSON.stringify(group))
    copyOfConditionGroup.sourceGroupId = uuidv4()
    copyOfConditionGroup.id = uuidv4()
    savedConditionGroupsArray.push(copyOfConditionGroup)

    ReactGA.event('condition_group_save', {
      groups: copyOfConditionGroup.conditionGroups.length,
      conditions: copyOfConditionGroup.conditions.length,
    })

    localStorage.setItem(
      `savedConditions-${tradingHouse}`,
      JSON.stringify(savedConditionGroupsArray)
    )

    setSnackbarSettings({
      severity: 'success',
      message: `Group saved!`,
      autoHideDuration: 3000,
    })
  }

  const handleDeleteGroup = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isReadOnly) return
    handleRemoveConditionGroup(group.id)
  }

  const handleCopyTo = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isReadOnly) return
    handleConditionGroupCopyTo(group.id)
  }

  // Handler for title click - enter edit mode
  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isReadOnly) return
    setIsEditingTitle(true)
  }

  // Handler for title change
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return
    setEditedTitle(e.target.value)
  }

  // Handler for title input blur and key press - save changes
  const handleTitleSave = () => {
    if (isReadOnly) return
    handleChangeConditionGroup(group.id, 'groupTitle', editedTitle)
    setIsEditingTitle(false)
  }

  // Handle key press events (Enter to save, Escape to cancel)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isReadOnly) return
    if (e.key === 'Enter') {
      handleTitleSave()
    } else if (e.key === 'Escape') {
      setEditedTitle(group.groupTitle || '')
      setIsEditingTitle(false)
    }
  }

  const theme = useTheme()

  const [chartDetailEditing, setChartDetailEditing] = useState(false)
  const [countSelectEditing, setCountSelectEditing] = useState(false)
  const [chartDetailHovering, setChartDetailHovering] = useState(false)
  const [countSelectHovering, setCountSelectHovering] = useState(false)

  return (
    <Box sx={{ mb: 2 }}>
      <GroupItem
        ref={dragDropRef}
        data-tour-id={isReadOnly ? 'trading-house-pattern-group' : 'user-pattern-group'}
        sx={{
          opacity: isDragging ? 0.5 : 1,
          border: highlightedIds.has(group.id)
            ? `1px solid ${theme.palette.primary.main}`
            : isOver && canDrop
              ? `2px dashed ${theme.palette.primary.main}`
              : `1px solid ${theme.palette.divider}`,
          boxShadow: highlightedIds.has(group.id)
            ? `0px 0px 8px ${alpha(theme.palette.primary.main, 0.4)}`
            : 'none',
          backgroundColor:
            isOver && canDrop
              ? alpha(theme.palette.primary.main, 0.1)
              : theme.palette.background.custom2,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        }}
      >
        {!isReadOnly && (
          <DragHandle>
            <DragIndicatorIcon fontSize="small" />
          </DragHandle>
        )}
        <ListItemText
          primary={
            <>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                <Typography variant="subtitle2" component="span" color="text.primary">
                  {algorithmicTitle}
                  {group.groupTitle || isEditingTitle ? ': ' : ''}
                </Typography>

                {isEditingTitle && !isReadOnly ? (
                  <input
                    autoFocus
                    value={editedTitle}
                    onChange={handleTitleChange}
                    onBlur={handleTitleSave}
                    onKeyDown={handleKeyPress}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      border: 'none',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      background: 'transparent',
                      color: theme.palette.text.primary,
                      fontSize: '1rem',
                      padding: '2px 4px',
                      width: '150px',
                      outline: 'none',
                    }}
                  />
                ) : (
                  <Typography
                    variant="subtitle2"
                    color="text.primary"
                    onClick={isReadOnly ? undefined : handleTitleClick}
                    sx={{
                      cursor: isReadOnly ? 'default' : 'pointer',
                      '&:hover': {
                        color: isReadOnly ? 'inherit' : theme.palette.primary.main,
                        textDecoration: isReadOnly ? 'none' : 'underline',
                      },
                    }}
                  >
                    {group.groupTitle || (isReadOnly ? '' : 'Add title...')}
                  </Typography>
                )}
              </Box>
            </>
          }
          secondary={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip
                title={
                  isReadOnly
                    ? 'Chart periodicity and timeframe for this condition group'
                    : 'Set the chart periodicity and timeframe for this condition group.'
                }
                open={chartDetailHovering && !chartDetailEditing}
              >
                <Box
                  onMouseEnter={() => setChartDetailHovering(true)}
                  onMouseLeave={() => setChartDetailHovering(false)}
                >
                  <ChartDetailInput
                    details={group}
                    handleChange={(key: string, value: any) =>
                      !isReadOnly && handleChangeConditionGroup(group.id, key, value)
                    }
                    readOnly={isReadOnly}
                    onEdit={setChartDetailEditing}
                  />
                </Box>
              </Tooltip>
              |
              <Tooltip
                title={`${isReadOnly ? 'Number of' : 'Set how many'} rules must be true for this group to be considered true.`}
                open={countSelectHovering && !countSelectEditing}
              >
                <Box
                  onMouseEnter={() => setCountSelectHovering(true)}
                  onMouseLeave={() => setCountSelectHovering(false)}
                >
                  <ConditionGroupCountSelect
                    groupId={group.id}
                    count={group.count}
                    totalConditions={totalConditions}
                    readOnly={isReadOnly}
                    onEdit={setCountSelectEditing}
                  />
                </Box>
              </Tooltip>
            </Box>
          }
        />
        {!isReadOnly && (
          <>
            <GroupActionButton
              size="small"
              onClick={() => handleSaveConditionGroup()}
              className="group-action-button"
              textcolor={theme.palette.primary.main}
              hoverbgcolor={theme.palette.primary.main + '20'}
            >
              <Tooltip title="Save group">
                <StarBorderIcon fontSize="small" />
              </Tooltip>
            </GroupActionButton>
            <GroupActionButton
              size="small"
              onClick={() => handleConditionGroupDuplicate(group.id)}
              className="group-action-button"
              textcolor={theme.palette.primary.main}
              hoverbgcolor={theme.palette.primary.main + '20'}
            >
              <Tooltip title="Duplicate group">
                <ContentCopy fontSize="small" />
              </Tooltip>
            </GroupActionButton>
            <GroupActionButton
              size="small"
              onClick={handleCopyTo}
              className="group-action-button"
              textcolor={theme.palette.primary.main}
              hoverbgcolor={theme.palette.primary.main + '20'}
            >
              <Tooltip title={`Copy to ${activeTab === 'buy' ? 'sell' : 'buy'}`}>
                <FlipCameraAndroidIcon fontSize="small" />
              </Tooltip>
            </GroupActionButton>
            <GroupActionButton
              size="small"
              onClick={handleDeleteGroup}
              className="group-action-button"
            >
              <Tooltip title="Remove group">
                <DeleteOutlineIcon fontSize="small" />
              </Tooltip>
            </GroupActionButton>
          </>
        )}
      </GroupItem>

      <Box sx={{ position: 'relative' }}>
        {/* Render conditions */}
        {group.conditions && group.conditions.length > 0 && (
          <ConditionContainer ref={ref}>
            {group.conditions.map((condition, index) => (
              <Box key={condition.id} sx={{ position: 'relative' }}>
                <ConditionListItem
                  condition={condition}
                  sourceGroupId={group.id}
                  isReadOnly={isReadOnly}
                />
                {!isReadOnly && (
                  <Tooltip title="Add condition" arrow>
                    <ConnectionDot onClick={() => handleAddCondition(group.id, undefined, index)} />
                  </Tooltip>
                )}
              </Box>
            ))}
          </ConditionContainer>
        )}

        {/* Render nested condition groups */}
        {group.conditionGroups && group.conditionGroups.length > 0 && (
          <GroupContainer>
            {group.conditionGroups.map((nestedGroup, index) => (
              <Box key={nestedGroup.id} sx={{ position: 'relative' }}>
                {/* Add a dot at the top of the first nested group */}
                {index === 0 && !isReadOnly && (
                  <Tooltip title="Add condition" arrow>
                    <ConnectionDot onClick={() => handleAddCondition(group.id)} />
                  </Tooltip>
                )}
                <GroupItemContainer>
                  <ConditionGroupListItem
                    group={nestedGroup}
                    depth={depth + 1}
                    isReadOnly={isReadOnly}
                  />
                </GroupItemContainer>
              </Box>
            ))}
          </GroupContainer>
        )}

        {!isReadOnly && <AddItemButton groupId={group.id} />}
      </Box>
    </Box>
  )
}

function PatternListItem({
  disableRowExpand,
  defaultOpen,
  isTradingHousePattern,
  isEditable,
  canCopy,
}: any) {
  const { usermeta, tradingHouse, canViewPrivatePatterns } = useApplicationSuiteContext()

  const {
    pattern,
    activeTab,
    setActiveTab,
    addConditionGroup,
    handleDrop,
    handleChangePattern,
    savePattern,
    isSavingPattern,
  } = useContext(PatternTreeContext)

  const { openSidePanel, selectedPattern, closeSidePanel } = usePatternSidePanelContext()

  const { selectedAlert, setSelectedAlert, closeSidePanel: closeAlertSidePanel } = useAlertContext()

  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [expanded, setExpanded] = useState(defaultOpen)
  const [alertCount, setAlertCount] = useState(0)
  const [backtestCount, setBacktestCount] = useState(0)
  const [isSharePatternDialogOpen, setIsSharePatternDialogOpen] = useState<boolean>(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [shareable, setShareable] = useState<any>(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState(pattern?.name || '')
  const [patternChanged, setPatternChanged] = useState(false)
  const [triggerChartHovering, setTriggerChartHovering] = useState(false)
  const [triggerChartEditing, setTriggerChartEditing] = useState(false)
  const [isSaveAsStandardPatternDialogOpen, setIsSaveAsStandardPatternDialogOpen] =
    useState<boolean>(false)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [duplicateName, setDuplicateName] = useState('')
  const [currentConditionGroups, setCurrentConditionGroups] = useState<ConditionGroupType[]>(
    pattern?.buyConditionGroups || []
  )
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set())

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // Initialize the save pattern hook to copy trading house patterns
  const { forceSave, hasPatternChanged } = useSavePattern(
    () => {}, // We don't need to update the pattern state here
    usermeta,
    tradingHouse,
    canViewPrivatePatterns
  )

  const { copyPattern } = useCopyPatternWithSave(forceSave)

  // Check if pattern has changed and cache the result
  useEffect(() => {
    let isMounted = true
    const checkPatternChanged = async () => {
      if (pattern) {
        const hasChanged = await hasPatternChanged(pattern)
        if (isMounted) {
          setPatternChanged(hasChanged)
        }
      }
    }

    checkPatternChanged()

    return () => {
      isMounted = false
    }
  }, [hasPatternChanged, pattern])

  useEffect(() => {
    if (pattern?.id) {
      getAlertsCount(pattern.id)
      getBacktestsCount(pattern.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern?.id])

  const getAlertsCount = async (patternId: string) => {
    try {
      const alertsRef = collection(firebase_firestore, 'tradingPlans')
      const q = query(
        alertsRef,
        where('pattern.id', '==', patternId),
        where('user.gcid', '==', usermeta.gcid)
      )
      const snapshot = await getDocs(q)
      const count = snapshot.docs.reduce((acc, docSnap) => {
        const data = docSnap.data() as { alertTypes?: unknown }
        const alertTypes = Array.isArray(data?.alertTypes) ? (data.alertTypes as unknown[]) : []
        const hasExitTradingPlan = alertTypes.includes('exitTradingPlan')
        return hasExitTradingPlan ? acc : acc + 1
      }, 0)
      setAlertCount(count)
    } catch (error) {
      console.error('Error getting alerts count:', error)
      setAlertCount(0)
    }
  }

  const getBacktestsCount = async (patternId: string) => {
    try {
      const backtestsRef = collection(firebase_firestore, `usermeta/${usermeta.gcid}/backtests`)
      const q = query(backtestsRef, where('pattern.id', '==', patternId))
      const snapshot = await getCountFromServer(q)
      setBacktestCount(snapshot.data().count)
    } catch (error) {
      console.error('Error getting backtests count:', error)
      setBacktestCount(0)
    }
  }

  // Root level drop functionality
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ['group'],
      canDrop: (item: any) => {
        // Only allow drops from the same pattern and not trading house patterns
        return !isTradingHousePattern && item.patternId === pattern?.id
      },
      drop: (item: any, monitor) => {
        if (isTradingHousePattern) return

        // Check if it's a nested drop
        if (monitor.didDrop()) {
          return
        }

        // Only handle drops from the same pattern
        if (item.patternId !== pattern?.id) {
          return
        }

        // Special case for root level drops
        if (item.type === 'group' && item.sourceGroupId) {
          // Use the handleDrop mechanism from PatternTreeContext but with a special flag
          // We use ROOT_GROUP as the target which is a special case in the handleDrop function
          handleDrop('ROOT_GROUP', item, 0)
        }
      },
      collect: (monitor) => ({
        isOver:
          !!monitor.isOver({ shallow: true }) &&
          monitor.getItem()?.patternId === pattern?.id &&
          !isTradingHousePattern,
        canDrop:
          !!monitor.canDrop() &&
          monitor.getItem()?.patternId === pattern?.id &&
          !isTradingHousePattern,
      }),
    }),
    [handleDrop, pattern?.id, isTradingHousePattern]
  )

  useEffect(() => {
    if (activeTab === 'buy') {
      setCurrentConditionGroups(pattern?.buyConditionGroups || [])
    } else {
      setCurrentConditionGroups(pattern?.sellConditionGroups || [])
    }
  }, [activeTab, pattern])

  // Check if pattern has any invalid conditions (including legacy CHANGED_* missing threshold)
  const hasInvalidConditions = (() => {
    if (!pattern) return false
    const allGroups = [
      ...(pattern.buyConditionGroups || []),
      ...(pattern.sellConditionGroups || []),
    ]
    const check = (groups: ConditionGroupType[]): boolean => {
      for (const group of groups) {
        for (const condition of group.conditions || []) {
          if (!condition.condition.isValid) return true
          // Check legacy CHANGED_* conditions missing threshold
          const ct = getConditionTypes(condition?.condition?.LHS?.field)?.find(
            (c) => c.value === condition?.condition?.conditionType
          )
          if (ct?.hasThreshold && condition?.condition?.threshold === undefined) return true
        }
        if (group.conditionGroups?.length && check(group.conditionGroups)) return true
      }
      return false
    }
    return check(allGroups)
  })()

  if (!pattern) {
    return (
      <PatternItemWrapper variant="outlined">
        <PatternHeader>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            Loading pattern...
          </Typography>
        </PatternHeader>
      </PatternItemWrapper>
    )
  }

  const handleToggle = () => {
    if (!disableRowExpand) {
      setExpanded(!expanded)
    }
  }

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (selectedPattern?.id === pattern.id) {
      closeSidePanel()
    } else if (pattern && !isTradingHousePattern) {
      if (selectedAlert) {
        setSelectedAlert(null)
        closeAlertSidePanel()
      }
      openSidePanel(pattern)
    }
  }

  async function sharePattern() {
    if (!shareable) {
      const shareable = await generatePatternShareable()
      if (shareable) {
        setShareable(shareable)
        setIsSharePatternDialogOpen(true)
      }
    } else {
      setIsSharePatternDialogOpen(true)
    }
  }

  async function generatePatternShareable() {
    if (!pattern || !pattern.id) {
      return
    }

    const shareable = await generateShareLink(
      pattern.id,
      `usermeta/${usermeta.gcid}/patterns/${pattern.id}`,
      'PATTERN',
      { name: pattern.name, createdBy: { gcid: usermeta?.gcid, email: usermeta?.email } }
    )
    return shareable //`${getMemberPortalUrl(tradingHouse, `/dashboard/strategy-suite?shareable=${shareLink.linkId}`)}`;
  }

  const saveAsStandardPattern = async () => {
    if (!usermeta.isAdmin) return
    if (!pattern) return
    try {
      const patternCopy = { ...pattern }
      delete patternCopy.id
      await forceSave(patternCopy, true)
      setSnackbarSettings({
        message: 'Pattern saved as standard pattern!',
        severity: 'success',
        autoHideDuration: 3000,
      })
    } catch (error) {
      console.error('Error saving pattern:', error)
      setSnackbarSettings({
        message: 'Error saving pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
  }

  const handleCopyPattern = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!pattern) return
    await copyPattern(pattern)
  }

  const handleDuplicatePattern = async () => {
    if (!pattern) return
    try {
      setSnackbarSettings({
        message: 'Duplicating pattern...',
        severity: 'info',
        autoHideDuration: 3000,
      })

      const patternCopy = JSON.parse(JSON.stringify(pattern))
      delete patternCopy.id
      patternCopy.name = duplicateName || `${pattern.name} (Copy)`
      patternCopy.version = undefined
      patternCopy.alertVersion = undefined

      // Regenerate all condition/group IDs and remap prerequisites
      regeneratePatternIds(patternCopy)

      const savedPattern = await forceSave(patternCopy)

      if (savedPattern) {
        ReactGA.event('pattern_duplicate', { pattern_name: patternCopy.name })
        setSnackbarSettings({
          message: 'Pattern duplicated successfully!',
          severity: 'success',
          autoHideDuration: 3000,
        })
      }
    } catch (error) {
      console.error('Error duplicating pattern:', error)
      setSnackbarSettings({
        message: 'Error duplicating pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
    setIsDuplicateDialogOpen(false)
  }

  // Handle deleting a pattern (soft delete)
  const handleDeletePattern = async () => {
    if (!pattern) return
    try {
      await deletePattern(pattern, isTradingHousePattern, usermeta)
      setSnackbarSettings({
        message: 'Pattern deleted successfully!',
        severity: 'success',
        autoHideDuration: 3000,
      })
    } catch (error) {
      console.error('Error deleting pattern:', error)
      setSnackbarSettings({
        message: 'Error deleting pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }

    // Close the dialog and side panel
    setDeleteDialogOpen(false)

    if (selectedPattern?.id === pattern.id) {
      closeSidePanel()
    }
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedName(e.target.value)
  }

  const handleNameSave = async () => {
    if (!pattern) return

    try {
      handleChangePattern('name', editedName)
    } catch (error) {
      console.error('Error updating pattern name:', error)
    } finally {
      setIsEditingName(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameSave()
    } else if (e.key === 'Escape') {
      setEditedName(pattern?.name || '')
      setIsEditingName(false)
    }
  }

  return (
    <Box
      className={isTradingHousePattern ? 'trading-house-patterns-section' : 'your-patterns-section'}
    >
      <PatternItemWrapper
        className="pattern-card"
        data-pattern-id={pattern.id}
        variant="outlined"
        sx={{
          backgroundColor: expanded
            ? theme.palette.background.custom2
            : theme.palette.background.custom1,
          borderColor:
            selectedPattern?.id === pattern.id ? theme.palette.primary.main : theme.palette.divider,
        }}
      >
        <PatternHeader
          onClick={handleToggle}
          data-tour-id={isTradingHousePattern ? 'trading-house-pattern' : 'user-pattern'}
        >
          {!disableRowExpand && (
            <IconButton
              size="small"
              sx={{
                padding: 0.5,
                marginRight: 1,
              }}
            >
              {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexGrow: 1,
              width: 0,
            }}
          >
            {isEditingName ? (
              <input
                autoFocus
                value={editedName}
                onChange={handleNameChange}
                onBlur={handleNameSave}
                onKeyDown={handleKeyPress}
                onClick={(e) => e.stopPropagation()}
                style={{
                  border: 'none',
                  borderBottom: `1px solid ${theme.palette.primary.main}`,
                  background: 'transparent',
                  color: theme.palette.text.primary,
                  fontSize: '1rem',
                  padding: '2px 4px',
                  width: '100%',
                  outline: 'none',
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.fontWeightMedium,
                }}
              />
            ) : (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                data-tour-id="pattern-name"
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'medium',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pattern.name || 'Unnamed Pattern'}
                </Typography>
                {pattern.source === 'strategy-agent' && (
                  <Tooltip title="Created by AI Session">
                    <Chip
                      icon={<SmartToyOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                      label="Agent"
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </Tooltip>
                )}
                {isEditable &&
                  !isTradingHousePattern &&
                  (selectedPattern?.id === pattern.id || expanded) && (
                    <Tooltip title="Edit pattern name" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          setIsEditingName(true)
                        }}
                        sx={{
                          opacity: 0.7,
                          '&:hover': {
                            opacity: 1,
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
              </Box>
            )}
          </Box>

          <ActionButtons onClick={(e) => e.stopPropagation()}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isTradingHousePattern && alertCount > 0 && (
                <Tooltip
                  title={`${alertCount} alert${alertCount !== 1 ? 's' : ''} using this pattern`}
                >
                  <Chip
                    icon={<NotificationsActiveIcon fontSize="small" />}
                    label={alertCount}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Tooltip>
              )}

              {!isTradingHousePattern && backtestCount > 0 && (
                <Tooltip
                  title={`${backtestCount} backtest${backtestCount !== 1 ? 's' : ''} created with this pattern`}
                >
                  <Chip
                    icon={<AssessmentIcon fontSize="small" />}
                    label={backtestCount}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Box>

            {canCopy || isTradingHousePattern ? (
              <Tooltip title="Copy to Account">
                <Button
                  onClick={handleCopyPattern}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: isSmallScreen ? '48px' : '180px',
                    minWidth: isSmallScreen ? '48px' : '180px',
                    color: theme.palette.primary.main,
                    transition: 'all 0.2s ease',
                  }}
                  endIcon={isSmallScreen ? null : <FileCopyIcon />}
                  data-tour-id="pattern-copy-button"
                >
                  {isSmallScreen ? <FileCopyIcon /> : 'Copy to Account'}
                </Button>
              </Tooltip>
            ) : isEditable ? (
              <Tooltip
                title={selectedPattern?.id === pattern.id ? 'Close Details' : 'View Details'}
              >
                <Button
                  onClick={handleSettingsClick}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: isSmallScreen ? '48px' : '140px',
                    minWidth: isSmallScreen ? '48px' : '140px',
                    color:
                      selectedPattern?.id === pattern.id
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                    transition: 'all 0.2s ease',
                  }}
                  endIcon={
                    isSmallScreen ? null : selectedPattern?.id === pattern.id ? (
                      <CloseIcon />
                    ) : (
                      <ArrowRightAlt />
                    )
                  }
                  data-tour-id="pattern-condition-button"
                >
                  {isSmallScreen ? (
                    selectedPattern?.id === pattern.id ? (
                      <CloseIcon />
                    ) : (
                      <ArrowRightAlt />
                    )
                  ) : selectedPattern?.id === pattern.id ? (
                    'Close Details'
                  ) : (
                    'View Details'
                  )}
                </Button>
              </Tooltip>
            ) : null}
          </ActionButtons>
        </PatternHeader>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <PatternContent data-tour-id="pattern-item">
            {/* Controls section */}
            <ControlsContainer>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                }}
              >
                <ToggleButtonGroup
                  color="primary"
                  value={activeTab}
                  exclusive
                  onChange={(e, newTab) => {
                    if (newTab) {
                      setActiveTab(newTab)
                    }
                  }}
                  aria-label="Pattern Type"
                  size="small"
                  data-tour-id="pattern-buy-sell-tab"
                >
                  <ToggleButton
                    value="buy"
                    aria-label="buy pattern"
                    sx={{ height: '40px', width: '70px' }}
                  >
                    BUY
                  </ToggleButton>
                  <ToggleButton
                    value="sell"
                    aria-label="sell pattern"
                    sx={{ height: '40px', width: '70px' }}
                  >
                    SELL
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Tooltip
                title="Set the chart periodicity and timeframe that triggers this pattern. This is the timeframe at which the pattern will be evaluated for its rules."
                open={triggerChartHovering && !triggerChartEditing}
              >
                <Box
                  onMouseEnter={() => setTriggerChartHovering(true)}
                  onMouseLeave={() => setTriggerChartHovering(false)}
                >
                  <TriggerChartSelector
                    readOnly={!isEditable || isTradingHousePattern}
                    onEdit={setTriggerChartEditing}
                  />
                </Box>
              </Tooltip>

              <Box
                sx={{
                  ml: 'auto',
                }}
              >
                {isTradingHousePattern && usermeta.isAdmin && (
                  <Box display={'flex'} gap={2} alignItems={'center'}>
                    <Tooltip title="Admin Only: Delete pattern">
                      <IconButton
                        onClick={() => setDeleteDialogOpen(true)}
                        size="small"
                        color={'error'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {isEditable && !isTradingHousePattern && (
                  <Box display={'flex'} gap={2} alignItems={'center'}>
                    <Box display={'flex'} gap={1} alignItems={'center'}>
                      {usermeta.isAdmin && (
                        <Tooltip title="Admin Only: Save as Standard Pattern">
                          <IconButton
                            onClick={() => setIsSaveAsStandardPatternDialogOpen(true)}
                            size="small"
                          >
                            <AdminPanelSettingsIcon fontSize={'small'} color={'warning'} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(patternChanged || isSavingPattern) && (
                        <Tooltip
                          title={
                            hasInvalidConditions
                              ? 'Fix invalid conditions before saving'
                              : 'Save pattern'
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() => !hasInvalidConditions && savePattern()}
                              size="small"
                              disabled={isSavingPattern || hasInvalidConditions}
                              data-tour-id="save-pattern-button"
                            >
                              {isSavingPattern ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Save fontSize={'small'} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={'Duplicate Pattern'}>
                        <IconButton
                          onClick={() => {
                            setDuplicateName(`${pattern?.name || 'Pattern'} (Copy)`)
                            setIsDuplicateDialogOpen(true)
                          }}
                          size="small"
                        >
                          <ContentCopy fontSize={'small'} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={'Share Pattern'}>
                        <IconButton onClick={() => sharePattern()} size="small">
                          <ReplyIcon sx={{ transform: 'scaleX(-1)' }} fontSize={'small'} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Tooltip title="Delete pattern">
                      <IconButton
                        onClick={() => setDeleteDialogOpen(true)}
                        size="small"
                        color={'error'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </ControlsContainer>

            {/* Condition Groups section - conditionally show based on selected pattern type */}
            <ConditionGroupsContainer
              ref={isEditable && !isTradingHousePattern ? drop : null}
              sx={{
                backgroundColor:
                  isOver && canDrop ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
                border: isOver && canDrop ? `2px dashed ${theme.palette.primary.main}` : 'none',
                borderRadius: 1,
                transition: 'background-color 0.2s, border 0.2s',
                padding: isOver && canDrop ? 2 : 0,
              }}
            >
              <HighlightedPrereqContext.Provider value={{ highlightedIds, setHighlightedIds }}>
                {currentConditionGroups.length > 0 ? (
                  <List sx={{ width: '100%', padding: 0 }}>
                    {currentConditionGroups.map((group: ConditionGroupType) => (
                      <ConditionGroupListItem
                        key={group.id}
                        group={group}
                        isReadOnly={!isEditable || isTradingHousePattern}
                      />
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No {activeTab.toUpperCase()} condition groups defined for this pattern.
                  </Typography>
                )}
              </HighlightedPrereqContext.Provider>

              {/* New Group Button - only show for user's patterns */}
              {isEditable && !isTradingHousePattern && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                  }}
                >
                  <Button
                    onClick={() => pattern && addConditionGroup()}
                    variant="text"
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      width: 'auto',
                      px: 2,
                    }}
                  >
                    New Group +
                  </Button>
                </Box>
              )}
            </ConditionGroupsContainer>
          </PatternContent>
        </Collapse>
      </PatternItemWrapper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-pattern-dialog-title"
        aria-describedby="delete-pattern-dialog-description"
      >
        <DialogTitle id="delete-pattern-dialog-title">Confirm Pattern Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-pattern-dialog-description">
            Are you sure you want to delete this pattern? All alerts and backtests associated with
            this pattern will still be available.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary" variant="contained">
            Cancel
          </Button>
          <Button onClick={handleDeletePattern} color="error" autoFocus variant="outlined">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ShareDialog
        open={!!isSharePatternDialogOpen}
        title={`Share this pattern`}
        onClose={() => setIsSharePatternDialogOpen(false)}
        shareable={shareable}
      />

      <Dialog
        open={!!isSaveAsStandardPatternDialogOpen && usermeta.isAdmin}
        onClose={() => setIsSaveAsStandardPatternDialogOpen(false)}
        aria-labelledby="save-as-standard-pattern-dialog-title"
        aria-describedby="save-as-standard-pattern-dialog-description"
      >
        <DialogTitle id="save-as-standard-pattern-dialog-title">
          Save as Standard Pattern
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="save-as-standard-pattern-dialog-description">
            Are you sure you want to save this pattern as a standard pattern? This will make it
            available to all traders.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsSaveAsStandardPatternDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              saveAsStandardPattern()
              setIsSaveAsStandardPatternDialogOpen(false)
            }}
            color="primary"
            variant="contained"
            autoFocus
            disabled={isSavingPattern}
            endIcon={isSavingPattern ? <CircularProgress size={20} /> : <Save fontSize={'small'} />}
          >
            Save as Standard Pattern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Pattern Dialog */}
      <Dialog
        open={isDuplicateDialogOpen}
        onClose={() => setIsDuplicateDialogOpen(false)}
        aria-labelledby="duplicate-pattern-dialog-title"
      >
        <DialogTitle id="duplicate-pattern-dialog-title">Duplicate Pattern</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a copy of this pattern with a new name. The duplicate will start at version 1
            with a clean version history.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Pattern Name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && duplicateName.trim()) {
                handleDuplicatePattern()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDuplicateDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDuplicatePattern}
            color="primary"
            variant="contained"
            disabled={!duplicateName.trim()}
            endIcon={<ContentCopy fontSize={'small'} />}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PatternListItem
export { ConditionListItem, ConditionGroupListItem, AddItemButton, HighlightedPrereqContext }
